"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, HeartHandshake, Zap, Brain, SunMedium, ShieldCheck, Calendar, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const benefits = [{
  title: "Energetische Balance",
  desc: "Harmonisierung des körpereigenen Energieflusses zur Lösung tiefer Blockaden.",
  icon: Zap
}, {
  title: "Emotionale Klärung",
  desc: "Sanfter Raum zum Loslassen alter Lasten und Festigen innerer Gelassenheit.",
  icon: HeartHandshake
}, {
  title: "Körperliches Wohlbefinden",
  desc: "Aktivierung der natürlichen Selbstheilungskräfte und Linderung von Anspannung.",
  icon: SunMedium
}, {
  title: "Mentale Klarheit",
  desc: "Beruhigung des Gedankenkarussells für mehr Fokus und bewusste Entscheidungen.",
  icon: Brain
}, {
  title: "Spirituelle Verbindung",
  desc: "Geerdete Rückverbindung zur eigenen Intuition und persönlichen Essenz.",
  icon: Sparkles
}, {
  title: "Ganzheitlicher Schutz",
  desc: "Stärkung der eigenen Resilienz und feinstofflichen Abgrenzung im Alltag.",
  icon: ShieldCheck
}];
const phases = [{
  number: "01",
  phaseLabel: "Phase 1",
  title: "Vorbereitung & Ankommen",
  desc: "In einem achtsamen Vorgespräch klären wir dein Anliegen, deine aktuellen Themen und stimmen den Raum ganz auf deine Bedürfnisse ein."
}, {
  number: "02",
  phaseLabel: "Phase 2",
  title: "Energetische Diagnose",
  desc: "Sanfte Erfassung des Chakren- und Energiezustands, um energetische Dysbalancen und Blockaden präzise zu lokalisieren."
}, {
  number: "03",
  phaseLabel: "Phase 3",
  title: "Heilsitzung & Behandlung",
  desc: "Gezielte Energieübertragung und Schwingungsausgleich zur Öffnung, Reinigung und Harmonisierung deiner Energiezentren."
}, {
  number: "04",
  phaseLabel: "Phase 4",
  title: "Integration & Nachruhe",
  desc: "Reflexion des Erlebten, Erdungsübungen und praktische Impulse zur nachhaltigen Verankerung der Wirkung in deinem Alltag."
}];
export default function BenefitsProcess() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1
    },
    animate: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  return <section id="benefitsandprocess" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-y border-border/40 isolate">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-10 items-start">
          
          <motion.div {...animationProps} className="space-y-8">
            <div className="space-y-3">
              <Badge variant="secondary" className="text-secondary-foreground font-normal tracking-wide">
                Wirkung & Mehrwert
              </Badge>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-[36px] font-normal leading-tight text-foreground">Vorteile der Chakra Heilung und Therapie</h2>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-xl">
                Erlebe eine tiefgreifende Regeneration für Körper, Geist und Seele durch achtsame energetische Harmonisierung im Einklang mit deiner inneren Natur.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((benefit, i) => {
              const IconComponent = benefit.icon;
              return <div key={benefit.title} data-index={i} className="p-4 rounded-xl border border-border/60 bg-card/60 backdrop-blur-xs flex flex-col justify-start transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-xs">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center justify-center size-8 rounded-lg bg-secondary text-secondary-foreground shrink-0">
                        <IconComponent className="size-4" />
                      </div>
                      <h3 className="font-sans text-base font-bold text-foreground">
                        {benefit.title}
                      </h3>
                    </div>
                    <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                      {benefit.title === "Ganzheitlicher Schutz" ? <Link to="/AngebotePage" className="underline cursor-pointer">
                          {benefit.desc}
                        </Link> : benefit.desc}
                    </p>
                  </div>;
            })}
            </div>

            <div className="pt-2">
              <Button asChild size="lg" className="shadow-xs gap-2">
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                  <span>Sitzung vereinbaren</span>
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div {...animationProps} transition={{
          duration: 0.5,
          ease: "easeOut",
          delay: shouldReduceMotion ? 0 : 0.15
        }} className="space-y-8 lg:pl-4">
            <div className="space-y-3">
              <Badge variant="secondary" className="text-secondary-foreground font-normal tracking-wide">
                Ablauf & Struktur
              </Badge>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-[36px] font-normal leading-tight text-foreground">4-Phasen-Sitzungsablauf: Chakren </h2>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-xl">
                Jede Behandlung folgt einer erprobten und transparenten Struktur, die Sicherheit, Vertrauen und nachhaltige Entfaltung ermöglicht.
              </p>
            </div>

            <div className="relative space-y-4">
              <div aria-hidden="true" className="hidden sm:block absolute left-6 top-6 bottom-6 w-px bg-border -z-10" />

              {phases.map((phase, i) => <Card key={phase.title} data-index={i} className="border-border/70 bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-sm">
                  <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row items-start gap-4">
                    <div className="flex items-center gap-3 sm:flex-col sm:items-center shrink-0">
                      <div className="flex items-center justify-center size-10 rounded-lg bg-secondary text-secondary-foreground font-sans font-bold text-sm shadow-2xs">
                        {phase.number}
                      </div>
                      <span className="sm:hidden text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {phase.phaseLabel}
                      </span>
                    </div>

                    <div className="space-y-1.5 flex-1">
                      <div className="hidden sm:flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {phase.phaseLabel}
                        </span>
                      </div>
                      <h3 className="font-sans text-base font-bold text-foreground">
                        {phase.title}
                      </h3>
                      <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                        {phase.desc}
                      </p>
                    </div>
                  </CardContent>
                </Card>)}
            </div>

            <div className="p-4 rounded-xl bg-muted/60 border border-border flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">Dauer: </span>
                ca. 75 bis 90 Minuten pro Einzelsitzung
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/AngebotePage">
                  <span>Mehr Details</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}